import React, { useState, useMemo } from 'react';
import { numFormat, cpfCnpj } from '@rededompedro/utils';
import { Button, Icon, Modal, Form } from 'semantic-ui-react';
import request from 'services/http';
import { tap, finalize, switchMap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import AnimatedModal from 'components/AnimatedModal';
import { useSubscription, maskCurrency } from 'utils';
import Table from 'components/Table';

export default function Demonstrativo({
    empresas,
    apuracao,
    setApuracao,
    periodos,
    form,
    clearState,
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingCorrecao, setIsSendingCorrecao] = useState(false);
    const [shouldDelete, setShouldDelete] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [lista, setLista] = useState(null);
    const [detailForm, setDetailForm] = useState({ cfop: '', ini: '', end: '' });
    const [saldoForm, setSaldoForm] = useState({
        ini: '',
        end: '',
        tipo: 1,
        descricao: '',
        valor: '',
    });

    if(shouldDelete){
        return null
    }
    function getList(event) {
        event.stopPropagation();
        event.preventDefault();
        const el = event.currentTarget;
        setShowModal(true);
        setDetailForm({
            cfop: el.getAttribute('data'),
            ini: el.getAttribute('ini'),
            end: el.getAttribute('end'),
        });
        setIsSubmitting(true);
    }

    function openForm(event) {
        event.stopPropagation();
        event.preventDefault();
        const el = event.currentTarget;
        setSaldoForm((state) => ({
            ...state,
            ini: el.getAttribute('ini'),
            end: el.getAttribute('end'),
        }));
        setShowForm(true);
    }

    function closeModal() {
        setShowModal(false);
        setTimeout(() => setLista(null), 300);
    }

    function closeForm() {
        setShowForm(false);
        setTimeout(
            () => setSaldoForm({ ini: '', end: '', tipo: 1, descricao: '', valor: '' }),
            300,
        );
    }

    function handleChange(_, { name, value }) {
        if (name === 'valor') value = maskCurrency(value);
        setSaldoForm((state) => ({ ...state, [name]: value }));
    }

    function deletarCorrecao(id) {
        const resp = window.confirm('Deseja realmente deletar essa correção?');
        if (resp) setShouldDelete(id);
    }
    // eslint-disable-next-line
    const detalharNFs$ = useMemo(
        () =>
            request({
                url: `/dre/apuracao-icms/detalhar/${form.empresa}/${detailForm.cfop}`,
                method: 'GET',
                params: {
                    ini: detailForm.ini,
                    end: detailForm.end,
                },
            }).pipe(
                tap(({ response }) => setLista(response.lista)),
                finalize(() => setIsSubmitting(false)),
            ),
        [detailForm, form.empresa],
    );
 // eslint-disable-next-line
    useSubscription(detalharNFs$, isSubmitting);
 // eslint-disable-next-line
    const submitForm$ = useMemo(
        () =>
            request({ url: '/dre/apuracao-icms', method: 'POST', body: form }).pipe(
                tap(({ response }) => setApuracao(response.periodo)),
                tap(() => closeForm()),
                finalize(() => {
                    setIsSendingCorrecao(false);
                    setShouldDelete(null);
                }),
            ),
        [form, setApuracao],
    );
 // eslint-disable-next-line
    const enviarCorrecao$ = useMemo(
        () =>
            request({
                url: `/dre/apuracao-icms/correcao/${form.empresa}`,
                method: 'POST',
                body: saldoForm,
            }).pipe(switchMap(() => submitForm$)),
        [saldoForm, form.empresa, submitForm$],
    );
 // eslint-disable-next-line
    useSubscription(enviarCorrecao$, isSendingCorrecao);

    let config = {
        footer: [
            {
                columns: [
                    {
                        colSpan: 6,
                        content: 'Total',
                        textAlign: 'center',
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista ? lista.map((x) => x.valor).reduce((a, b) => a + b, 0) : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista
                                ? lista.map((x) => x.valor_desconto).reduce((a, b) => a + b, 0)
                                : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista
                                ? lista.map((x) => x.valor_acrescimo).reduce((a, b) => a + b, 0)
                                : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista ? lista.map((x) => x.valor_frete).reduce((a, b) => a + b, 0) : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista ? lista.map((x) => x.valor_icms).reduce((a, b) => a + b, 0) : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista ? lista.map((x) => x.valor_ipi).reduce((a, b) => a + b, 0) : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista ? lista.map((x) => x.valor_subst).reduce((a, b) => a + b, 0) : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                    {
                        content: numFormat(
                            lista
                                ? lista.map((x) => x.valor_despesas).reduce((a, b) => a + b, 0)
                                : 0,
                        ),
                        style: { fontWeight: 'bold' },
                    },
                ],
            },
        ],
        columns: [
            { field: 'data', text: 'Data', sort: true },
            {
                field:
                    detailForm.cfop !== '' && parseInt(detailForm.cfop) > 5000
                        ? 'cliente'
                        : 'emitente',
                text:
                    detailForm.cfop !== '' && parseInt(detailForm.cfop) > 5000
                        ? 'Cliente'
                        : 'Emitente',
                sort: true,
            },
            { field: 'numero_nota', text: 'NF', sort: true },
            { field: 'codigo_ncm', text: 'NCM', sort: true },
            { field: 'cst', text: 'CST', sort: true },
            { field: 'descricao', text: 'Descrição', sort: true },
            { field: 'valor', text: 'Valor', sort: true, formatter: (value) => numFormat(value) },
            {
                field: 'valor_desconto',
                text: 'Vl. Descto',
                sort: true,
                formatter: (value) => numFormat(value),
            },
            {
                field: 'valor_acrescimo',
                text: 'Vl. Acres.',
                sort: true,
                formatter: (value) => numFormat(value),
            },
            {
                field: 'valor_frete',
                text: 'Vl. Frete',
                sort: true,
                formatter: (value) => numFormat(value),
            },
            {
                field: 'valor_icms',
                text: 'Vl. ICMS',
                sort: true,
                formatter: (value) => numFormat(value),
            },
            {
                field: 'valor_ipi',
                text: 'Vl. IPI',
                sort: true,
                formatter: (value) => numFormat(value),
            },
            {
                field: 'valor_subst',
                text: 'Vl. Sub. ST',
                sort: true,
                formatter: (value) => numFormat(value),
            },
            {
                field: 'valor_despesas',
                text: 'Outr. Desp.',
                sort: true,
                formatter: (value) => numFormat(value),
            },
        ],
        data: lista ? lista : [],
        exportData: true,
        filter: true,
    };

    if (detailForm.cfop !== '' && parseInt(detailForm.cfop) > 5000)
        config = Object.assign(config, { pagination: { perPage: 100 } });

    return (
        <>
            <div className="d-flex w-75 d-print-none mb-3">
                <Button size="mini" className="ml-auto btn-primary" onClick={() => window.print()}>
                    <Icon name="print" /> Imprimir
                </Button>
                <Button size="mini" className="mr-0 btn-red" onClick={() => clearState()}>
                    <Icon name="arrow left" /> Voltar
                </Button>
            </div>
            {apuracao &&
                apuracao.length > 0 &&
                apuracao.map((periodo, k) => (
                    <table
                        key={k}
                        className="demonstrativo w-75 border border-dark page-break"
                        style={{ marginBottom: 50 }}
                    >
                        <thead>
                            <tr className="border border-dark p-2">
                                <th
                                    colSpan="7"
                                    className="text-center"
                                    style={{ letterSpacing: 15 }}
                                >
                                    APURAÇÃO DO ICMS
                                </th>
                            </tr>
                            <tr>
                                <td className="p-2" colSpan="7">
                                    <div className="row">
                                        <div className="col-2">Empresa:</div>
                                        <div className="col">
                                            {empresas &&
                                                empresas.find((x) => x && x._id === form.empresa)
                                                    .nomeEmpresa}
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-2">CNPJ:</div>
                                        <div className="col">
                                            {empresas &&
                                                cpfCnpj(
                                                    empresas.find(
                                                        (x) => x && x._id === form.empresa,
                                                    ).cnpjPrincipal,
                                                )}
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-2">Periodo:</div>
                                        <div className="col">
                                            {periodos && form.periodo !== ''
                                                ? periodos.find((x) => x && x.id === form.periodo)
                                                      .descricao +
                                                  ' - ' +
                                                  periodo.iniHR +
                                                  ' à ' +
                                                  periodo.endHR
                                                : form.dataIni + ' à ' + form.dataFim}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </thead>

                        <tbody>
                            <tr className="border border-dark p-2">
                                <th
                                    colSpan="7"
                                    className="text-center"
                                    style={{ letterSpacing: 15 }}
                                >
                                    ENTRADAS
                                </th>
                            </tr>
                            <tr className="border border-dark p-2">
                                <td colSpan="2" className="text-center">
                                    Codificação
                                </td>
                                <td rowSpan="3" className="border border-dark text-center">
                                    Valores Contábeis
                                </td>
                                <td colSpan="4" className="text-center">
                                    ICMS - Valores Fiscais
                                </td>
                            </tr>
                            <tr className="border border-dark p-2">
                                <td rowSpan="2" className="border border-dark text-center">
                                    Contábil
                                </td>
                                <td rowSpan="2" className="border border-dark text-center">
                                    Fiscal
                                </td>
                                <td colSpan="2" className="border border-dark text-center">
                                    Operação c/ Crédito do Imposto
                                </td>
                                <td colSpan="2" className="border border-dark text-center">
                                    Operação s/ Crédito do Imposto
                                </td>
                            </tr>
                            <tr className="border border-dark p-2">
                                <td className="border border-dark text-center">Base de Cálculo</td>
                                <td className="border border-dark text-center">
                                    Imposto Creditado
                                </td>
                                <td className="border border-dark text-center">
                                    Isento / N. Tributado
                                </td>
                                <td className="border border-dark text-center">Outras</td>
                            </tr>

                            {periodo.creditos.map((cred, idx) => (
                                <tr key={idx}>
                                    <td className="border border-dark border-top-0 border-bottom-0"></td>
                                    <td className="border border-dark border-top-0 border-bottom-0 text-center">
                                        <a
                                            style={{ color: 'black' }}
                                            href="#/"
                                            data={cred.cfop}
                                            ini={periodo.init}
                                            end={periodo.end}
                                            onClick={getList}
                                        >
                                            {cred.cfop}
                                        </a>
                                    </td>
                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                        {numFormat(cred.valor)}
                                    </td>
                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                        {numFormat(cred.base)}
                                    </td>
                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                        {numFormat(cred.icms)}
                                    </td>
                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                        {numFormat(cred.ntrib)}
                                    </td>
                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                        {numFormat(cred.outros)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>

                        <tfoot>
                            <tr className="border border-dark p-2">
                                <td colSpan="2" className="text-center border border-dark">
                                    <div className="row">
                                        <div className="col">
                                            <strong>SUB-TOTAIS</strong>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">1.000 Do Estado</div>
                                    </div>
                                    <div className="row">
                                        <div className="col">2.000 De Outros Estados</div>
                                    </div>
                                    <div className="row">
                                        <div className="col">3.000 Do Exterior</div>
                                    </div>
                                </td>

                                {['valor', 'base', 'icms', 'ntrib', 'outros'].map((tp, j) => (
                                    <td
                                        key={j}
                                        colSpan="1"
                                        className="text-right border border-dark"
                                    >
                                        {['1', '2', '3'].map((i, k) => (
                                            <div key={k} className={k === 0 ? 'row mt-4' : 'row'}>
                                                <div className="col pr-4">
                                                    {numFormat(
                                                        periodo.creditos
                                                            .filter(
                                                                (x) =>
                                                                    x.cfop.toString().charAt(0) ===
                                                                    i,
                                                            )
                                                            .map((x) => x[tp])
                                                            .reduce((a, b) => a + b, 0),
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </td>
                                ))}
                            </tr>

                            <tr className="border border-dark p-2">
                                <th colSpan="2" className="text-center border border-dark">
                                    TOTAIS
                                </th>
                                {['valor', 'base', 'icms', 'ntrib', 'outros'].map((tp, idx) => (
                                    <th key={idx} className="text-right border border-dark pr-3">
                                        {numFormat(
                                            periodo.creditos
                                                .map((x) => x[tp])
                                                .reduce((a, b) => a + b, 0),
                                        )}
                                    </th>
                                ))}
                            </tr>

                            <tr>
                                <td colSpan="7" className="td-table">
                                    <table className="w-100 ">
                                        <tbody>
                                            <tr>
                                                <th
                                                    colSpan="7"
                                                    className="text-center"
                                                    style={{ letterSpacing: 15 }}
                                                >
                                                    SAÍDAS
                                                </th>
                                            </tr>
                                            <tr className="border border-dark border-left-0 border-right-0 p-2">
                                                <td colSpan="2" className="text-center">
                                                    Codificação
                                                </td>
                                                <td
                                                    rowSpan="3"
                                                    className="border border-dark text-center"
                                                >
                                                    Valores Contábeis
                                                </td>
                                                <td colSpan="4" className="text-center">
                                                    ICMS - Valores Fiscais
                                                </td>
                                            </tr>
                                            <tr className="border border-dark border-left-0 border-right-0 p-2">
                                                <td
                                                    rowSpan="2"
                                                    className=" text-center border-right border-dark"
                                                >
                                                    Contábil
                                                </td>
                                                <td rowSpan="2" className="text-center">
                                                    Fiscal
                                                </td>
                                                <td
                                                    colSpan="2"
                                                    className="text-center border-right border-dark"
                                                >
                                                    Operação c/ Débito do Imposto
                                                </td>
                                                <td colSpan="2" className="text-center">
                                                    Operação s/ Débito do Imposto
                                                </td>
                                            </tr>
                                            <tr className="border border-dark border-right-0 p-2">
                                                <td className="text-center border-right border-dark">
                                                    Base de Cálculo
                                                </td>
                                                <td className="text-center border-right border-dark">
                                                    Imposto Debitado
                                                </td>
                                                <td className="text-center border-right border-dark">
                                                    Isento / N. Tributado
                                                </td>
                                                <td className="text-center">Outras</td>
                                            </tr>

                                            {periodo.debitos.map((deb, idx) => (
                                                <tr key={idx}>
                                                    <td className="border border-dark border-top-0 border-bottom-0 border-left-0"></td>
                                                    <td className="border border-dark border-top-0 border-bottom-0 text-center">
                                                        <a
                                                            style={{ color: 'black' }}
                                                            href="#/"
                                                            data={deb.cfop}
                                                            ini={periodo.init}
                                                            end={periodo.end}
                                                            onClick={getList}
                                                        >
                                                            {deb.cfop}
                                                        </a>
                                                    </td>
                                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                                        {numFormat(deb.valor)}
                                                    </td>
                                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                                        {numFormat(deb.base)}
                                                    </td>
                                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                                        {numFormat(deb.icms)}
                                                    </td>
                                                    <td className="border border-dark border-top-0 border-bottom-0 text-right pr-3">
                                                        {numFormat(deb.ntrib)}
                                                    </td>
                                                    <td className="border border-dark border-top-0 border-bottom-0 border-right-0 text-right pr-3">
                                                        {numFormat(deb.outros)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>

                                        <tfoot>
                                            <tr className="p-2">
                                                <td
                                                    colSpan="2"
                                                    className="text-center border border-left-0 border-dark"
                                                >
                                                    <div className="row">
                                                        <div className="col">
                                                            <strong>SUB-TOTAIS</strong>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col">
                                                            5.000 Para o Estado
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col">
                                                            6.000 Para Outros Estados
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col">
                                                            7.000 Para o Exterior
                                                        </div>
                                                    </div>
                                                </td>
                                                {['valor', 'base', 'icms', 'ntrib', 'outros'].map(
                                                    (tp, j) => (
                                                        <td
                                                            key={j}
                                                            colSpan="1"
                                                            className={
                                                                tp === 'outros'
                                                                    ? 'text-right border border-dark border-right-0'
                                                                    : 'text-right border border-dark'
                                                            }
                                                        >
                                                            {['5', '6', '7'].map((i, k) => (
                                                                <div
                                                                    key={k}
                                                                    className={
                                                                        k === 0 ? 'row mt-4' : 'row'
                                                                    }
                                                                >
                                                                    <div className="col pr-4">
                                                                        {numFormat(
                                                                            periodo.debitos
                                                                                .filter(
                                                                                    (x) =>
                                                                                        x.cfop
                                                                                            .toString()
                                                                                            .charAt(
                                                                                                0,
                                                                                            ) === i,
                                                                                )
                                                                                .map((x) => x[tp])
                                                                                .reduce(
                                                                                    (a, b) => a + b,
                                                                                    0,
                                                                                ),
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </td>
                                                    ),
                                                )}
                                            </tr>

                                            <tr className="p-2">
                                                <th
                                                    colSpan="2"
                                                    className="text-center border border-dark border-left-0"
                                                >
                                                    TOTAIS
                                                </th>
                                                {['valor', 'base', 'icms', 'ntrib', 'outros'].map(
                                                    (tp, idx) => (
                                                        <th
                                                            key={idx}
                                                            className={
                                                                tp === 'outros'
                                                                    ? 'text-right border border-dark border-right-0 pr-3'
                                                                    : 'text-right border border-dark pr-3'
                                                            }
                                                        >
                                                            {numFormat(
                                                                periodo.debitos
                                                                    .map((x) => x[tp])
                                                                    .reduce((a, b) => a + b, 0),
                                                            )}
                                                        </th>
                                                    ),
                                                )}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </td>
                            </tr>

                            <tr>
                                <td colSpan="7" className="td-table">
                                    <table className="w-100">
                                        <tbody>
                                            <tr>
                                                <th
                                                    colSpan="7"
                                                    className="text-center"
                                                    style={{ letterSpacing: 15 }}
                                                >
                                                    APURAÇÃO DO SALDO
                                                </th>
                                            </tr>
                                            <tr className="border border-dark border-left-0 border-right-0 p-2">
                                                <td colSpan="6" className="text-center">
                                                    DÉBITOS E CRÉDITOS DO IMPOSTO
                                                </td>
                                                <td className="border border-dark border-right-0 text-center">
                                                    VALORES
                                                    <Button
                                                        style={{
                                                            padding: '.3rem',
                                                            margin: '3px 5px 3px auto',
                                                        }}
                                                        ini={periodo.init}
                                                        end={periodo.end}
                                                        className="d-print-none"
                                                        size="tiny"
                                                        color="green"
                                                        floated="right"
                                                        onClick={openForm}
                                                    >
                                                        Correção
                                                    </Button>
                                                </td>
                                            </tr>

                                            {periodo.apuracaoSaldo.map((apur, idx) => (
                                                <tr key={idx} className="p-2">
                                                    <td className={apur.className} colSpan="6">
                                                        {apur.text}{' '}
                                                        {apur.del && (
                                                            <Button
                                                                onClick={() =>
                                                                    deletarCorrecao(apur.id)
                                                                }
                                                                className="d-print-none"
                                                                style={{
                                                                    padding: '.25rem',
                                                                    marginLeft: 4,
                                                                }}
                                                                icon="times"
                                                                size="tiny"
                                                                color="red"
                                                            />
                                                        )}
                                                    </td>
                                                    <td
                                                        className={
                                                            apur.text === 'Total'
                                                                ? 'border-dark border-left border-top font-weight-bold text-right pr-3'
                                                                : 'border-dark border-left text-right pr-3'
                                                        }
                                                    >
                                                        {apur.value || apur.value === 0
                                                            ? numFormat(apur.value)
                                                            : ''}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                ))}
            <AnimatedModal open={showModal}>
                <Modal
                    open={true}
                    dimmer="blurring"
                    size="fullscreen"
                    closeIcon
                    onClose={() => closeModal()}
                >
                    <Modal.Header>
                        {empresas && empresas.find((x) => x && x._id === form.empresa).nomeEmpresa}{' '}
                        - CFOP: {detailForm.cfop.replace(/(\d)(?=(\d{3}))/g, '$1.')} / Periodo:{' '}
                        {DateTime.fromFormat(detailForm.ini.toString(), 'yyyyMMdd').toFormat(
                            'dd/MM/yyyy',
                        )}{' '}
                        -{' '}
                        {DateTime.fromFormat(detailForm.end.toString(), 'yyyyMMdd').toFormat(
                            'dd/MM/yyyy',
                        )}
                    </Modal.Header>
                    <Modal.Content>
                        {!isSubmitting && lista && (
                            <Table id="demonstrativo-det" compact sortable selectable {...config} />
                        )}
                        {isSubmitting && <p>Carregando...</p>}
                    </Modal.Content>
                </Modal>
            </AnimatedModal>
            <AnimatedModal open={showForm}>
                <Modal open={true} size="small" closeIcon onClose={() => closeForm()}>
                    <Modal.Header>
                        {empresas && empresas.find((x) => x && x._id === form.empresa).nomeEmpresa}{' '}
                        - Periodo:{' '}
                        {DateTime.fromFormat(saldoForm.ini.toString(), 'yyyyMMdd').toFormat(
                            'dd/MM/yyyy',
                        )}{' '}
                        -{' '}
                        {DateTime.fromFormat(saldoForm.end.toString(), 'yyyyMMdd').toFormat(
                            'dd/MM/yyyy',
                        )}
                    </Modal.Header>
                    <Modal.Content>
                        <h4 className="text-center">Adicionar correção de saldo</h4>
                        <Form
                            size="mini"
                            onSubmit={() => setIsSendingCorrecao(true)}
                            loading={isSendingCorrecao}
                            noValidate
                        >
                            <div className="row">
                                <div className="col col-lg-6 offset-lg-3">
                                    <Form.Input
                                        label="Valor"
                                        required
                                        name="valor"
                                        value={saldoForm.valor}
                                        placeholder="Informe o valor"
                                        autoComplete="off"
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col col-lg-6 offset-lg-3">
                                    <Form.Select
                                        label="Tipo"
                                        required
                                        name="tipo"
                                        value={saldoForm.tipo}
                                        placeholder="Tipo de operação"
                                        onChange={handleChange}
                                        options={[
                                            { text: 'Crédito', value: 1 },
                                            { text: 'Débito', value: 2 },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col col-lg-6 offset-lg-3">
                                    <Form.Input
                                        label="Motivo"
                                        required
                                        name="descricao"
                                        value={saldoForm.descricao}
                                        placeholder="Informe um motivo"
                                        onChange={handleChange}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col col-lg-6 offset-lg-3">
                                    <Button
                                        disabled={
                                            !!!saldoForm.valor ||
                                            !!!saldoForm.tipo ||
                                            !!!saldoForm.descricao
                                        }
                                        color="green"
                                        size="small"
                                    >
                                        <Icon name="send" />
                                        Enviar
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    </Modal.Content>
                </Modal>
            </AnimatedModal>
        </>
    );
}
